import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, CircularProgress, Button, Typography } from '@mui/material';
import Front from '@frontapp/plugin-sdk';
import { MoLogoWithText } from '@motivo/guanyin/src/components';
import { setAuthTokens } from '../utils/auth';
import { useAuth } from '../context/AuthContext';

export function generateRandomString(length: number): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);

  let result = '';
  for (let i = 0; i < length; i++) {
    result += charset[randomValues[i] % charset.length];
  }

  return result;
}

export async function sha256(value: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(value);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => {
      const hex = b.toString(16);
      return hex.padStart(2, '0');
    })
    .join('');

  return hashHex;
}

const API_URL = import.meta.env.VITE_API_URL;
const POLL_INTERVAL_MS = 3 * 1000;
const TIMEOUT_MS = 3 * 60 * 1000;

function Login() {
  const history = useHistory();
  const { refreshAuthState } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initiateLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const { origin } = window.location;

      const sessionId = crypto.randomUUID();
      const codeVerifier = generateRandomString(64);
      const codeChallenge = await sha256(codeVerifier);

      const createResponse = await fetch(`${API_URL}/auth/session/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          codeChallenge,
          origin,
        }),
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create auth session');
      }

      const authUrlResponse = await fetch(`${API_URL}/auth/authorizationUrl`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          origin,
          isHeimdall: true,
          sessionId,
        }),
      });

      if (!authUrlResponse.ok) {
        throw new Error('Failed to get authorization URL');
      }

      const { authorizationUrl } = await authUrlResponse.json();

      Front.openUrl(authorizationUrl);

      let timeoutHandle: ReturnType<typeof setTimeout>;
      const pollInterval = setInterval(async () => {
        try {
          const checkResponse = await fetch(`${API_URL}/auth/session/check/${sessionId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              codeVerifier,
              origin,
            }),
          });

          const data = await checkResponse.json();

          if (data.success && data.tokens) {
            clearInterval(pollInterval);
            clearTimeout(timeoutHandle);

            setAuthTokens({
              accessToken: data.tokens.accessToken,
              refreshToken: data.tokens.refreshToken,
            });

            refreshAuthState();

            setLoading(false);
            history.push('/');
          } else if (!checkResponse.ok && checkResponse.status === 403) {
            clearInterval(pollInterval);
            clearTimeout(timeoutHandle);
            throw new Error(data.error || 'Authentication verification failed');
          }
        } catch (err) {
          clearInterval(pollInterval);
          clearTimeout(timeoutHandle);
          setError(err instanceof Error ? err.message : 'Authentication failed');
          setLoading(false);
        }
      }, POLL_INTERVAL_MS);

      timeoutHandle = setTimeout(() => {
        clearInterval(pollInterval);
        setError('Authentication timed out. Please try again.');
        setLoading(false);
      }, TIMEOUT_MS);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      p={3}
    >
      <Box mb={3}>
        <MoLogoWithText text="Zeus" width={200} />
      </Box>

      {error && (
        <Typography color="error" mb={2}>
          {error}
        </Typography>
      )}

      <Button variant="contained" color="primary" onClick={initiateLogin} disabled={loading}>
        {loading ? <CircularProgress size={24} /> : 'Log in'}
      </Button>
    </Box>
  );
}

export default Login;
